/**
 * FeatureFlagProvider
 *
 * React Context provider for feature flags using PostHog.
 * Provides hooks for checking feature flags throughout the app.
 *
 * Usage:
 *   const isEnabled = useFeatureFlag('my-feature');
 *   const variant = useFeatureFlagVariant('button-color');
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import {
  isFeatureEnabled,
  getFeatureFlag,
  reloadFeatureFlags,
  identifyUser,
  isPostHogEnabled
} from '../../config/posthog.config';
import { useAuth } from './AuthProvider';
import { logger } from '../../utils/logger';

// Feature flag names - allow any string for flexibility with PostHog
export type FeatureFlagName = string;

interface FeatureFlagContextValue {
  /**
   * Check if a feature flag is enabled (boolean flags)
   */
  isEnabled: (flagName: FeatureFlagName) => boolean;

  /**
   * Get the value of a feature flag (for multivariate flags)
   */
  getValue: (flagName: FeatureFlagName) => string | boolean | undefined;

  /**
   * Whether feature flags have finished loading
   */
  isLoading: boolean;

  /**
   * Force reload feature flags from PostHog
   */
  reload: () => Promise<void>;

  /**
   * Whether PostHog is enabled
   */
  isPostHogActive: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [flagsReady, setFlagsReady] = useState(false);

  // V2 auth state - user data from AuthProvider
  const { user: authUser } = useAuth();

  // User identification data from V2 auth
  const userId = authUser?.id;
  const userEmail = authUser?.email;
  const userName = authUser?.name;
  // Get first org from V2 auth
  const firstOrgId = authUser?.orgIds?.[0];
  const firstOrgName = firstOrgId ? authUser?.orgs?.[firstOrgId]?.name : undefined;

  const isPostHogActive = isPostHogEnabled();

  // Identify user when they log in or org changes
  useEffect(() => {
    if (!isPostHogActive || !userId) {
      setIsLoading(false);
      return;
    }

    // Identify user with PostHog
    identifyUser(userId, {
      email: userEmail ?? undefined,
      name: userName ?? undefined,
      org_id: firstOrgId,
      org_name: firstOrgName
    });

    // Reload flags after identify to get user-specific flags
    reloadFeatureFlags().then(() => {
      setFlagsReady(true);
      setIsLoading(false);
      logger.info('[FeatureFlags] Flags loaded for user:', userId);
    });
  }, [userId, userEmail, userName, firstOrgId, firstOrgName, isPostHogActive]);

  // If PostHog isn't active, just mark as ready
  useEffect(() => {
    if (!isPostHogActive) {
      setIsLoading(false);
      setFlagsReady(true);
    }
  }, [isPostHogActive]);

  const isEnabled = useCallback(
    (flagName: FeatureFlagName): boolean => {
      if (!isPostHogActive) {
        // Return default values when PostHog is disabled
        return getDefaultFlagValue(flagName);
      }

      const result = isFeatureEnabled(flagName);
      return result ?? getDefaultFlagValue(flagName);
    },
    [isPostHogActive, flagsReady]
  );

  const getValue = useCallback(
    (flagName: FeatureFlagName): string | boolean | undefined => {
      if (!isPostHogActive) {
        return getDefaultFlagValue(flagName);
      }

      return getFeatureFlag(flagName);
    },
    [isPostHogActive, flagsReady]
  );

  const reload = useCallback(async () => {
    if (!isPostHogActive) return;

    setIsLoading(true);
    await reloadFeatureFlags();
    setIsLoading(false);
    logger.info('[FeatureFlags] Flags reloaded');
  }, [isPostHogActive]);

  const value: FeatureFlagContextValue = {
    isEnabled,
    getValue,
    isLoading,
    reload,
    isPostHogActive
  };

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

/**
 * Get default values for feature flags when PostHog is disabled
 * This allows features to work in development without PostHog
 *
 * Add defaults here for flags that should be enabled when PostHog is unavailable
 */
function getDefaultFlagValue(flagName: FeatureFlagName): boolean {
  const defaults: Record<string, boolean> = {
    // Add new flag defaults here as needed:
    // 'my-feature': true,
  };

  return defaults[flagName] ?? false;
}

/**
 * Hook to access feature flag context
 */
export const useFeatureFlags = (): FeatureFlagContextValue => {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }

  return context;
};

/**
 * Simple hook to check if a feature flag is enabled
 * Returns boolean (defaults to false if flag not found)
 */
export const useFeatureFlag = (flagName: FeatureFlagName): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagName);
};

/**
 * Hook to get a feature flag value (for multivariate flags)
 */
export const useFeatureFlagVariant = (flagName: FeatureFlagName): string | boolean | undefined => {
  const { getValue } = useFeatureFlags();
  return getValue(flagName);
};

/**
 * Hook to check if feature flags are still loading
 */
export const useFeatureFlagsLoading = (): boolean => {
  const { isLoading } = useFeatureFlags();
  return isLoading;
};

export { FeatureFlagContext };
